import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { IconComponent } from '../icons/icon-reusable';
import type { IconDef } from '../icons/icon';

type FormCardSectionSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-form-card-section',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './form-card-section.html',
})
export class FormCardSection {
  @Input() step: number | string | IconDef = 1;
  @Input() heading = '';
  @Input() subheading = '';
  @Input() size: FormCardSectionSize = 'lg';
  @Input() contentClassOverride = '';

  get isStepIcon(): boolean {
    return typeof this.step === 'object' && this.step !== null && 'svg' in this.step;
  }

  get stepLabel(): string {
    return this.isStepIcon ? '' : String(this.step);
  }

  get stepIcon(): IconDef | null {
    return this.isStepIcon ? (this.step as IconDef) : null;
  }

  get cardClass(): string {
    if (this.size === 'sm') return 'rounded-lg border border-slate-200 bg-white p-6 shadow-sm mb-4';
    if (this.size === 'md') return 'rounded-lg border border-slate-200 bg-white p-6 shadow-sm mb-5';
    return 'rounded-lg border border-slate-200 bg-white p-5 shadow-sm mb-6';
  }

  get headerClass(): string {
    return 'flex flex-col gap-3 md:flex-row md:items-start md:justify-between';
  }

  get stepClass(): string {
    const base = 'flex items-center justify-center text-xs font-semibold';

    if (this.size === 'sm') {
      return `${base} ${this.isStepIcon ? 'w-[36px] h-[36px]' : 'w-[24px] h-[24px]'} rounded-md bg-indigo100 text-indigo600 mt-1`;
    }
    if (this.size === 'md') {
      return `${base} ${this.isStepIcon ? 'w-[36px] h-[36px]' : 'w-[28px] h-[28px]'} rounded-md bg-indigo100 text-indigo600 mt-1`;
    }

    if (this.isStepIcon) {
      return `${base} w-[40px] h-[40px] rounded-md bg-indigo100 text-indigo600 mt-1`;
    }

    return `${base} w-[30px] h-[30px] rounded-md bg-indigo100 text-indigo600 mt-2`;
  }

  get headingClass(): string {
    if (this.size === 'sm') return 'text-sm font-semibold text-slate900';
    if (this.size === 'md') return 'text-base font-semibold text-slate900';
    return 'text-lg font-semibold text-slate900';
  }

  get subheadingClass(): string {
    return 'mt-1 text-xs text-slate500';
  }

  get contentClass(): string {
    if (this.contentClassOverride) return this.contentClassOverride;
    if (this.size === 'sm') return 'mt-3 rounded-lg border border-slate200 p-3';
    if (this.size === 'md') return 'mt-3 rounded-lg border border-slate200 p-3.5';
    return 'mt-4 rounded-lg border border-slate200 p-4';
  }
}
